/**
 * Image preprocessing utilities for barcode scanner
 * Improves readability by enhancing contrast, brightness, and clarity
 */

export interface PreprocessingOptions {
  contrast?: number; // 0-3 (1 = no change)
  brightness?: number; // -100 to 100
  saturation?: number; // 0-2 (1 = no change)
  blur?: number; // 0-5 (0 = no blur)
  sharpen?: number; // 0-1 (0 = no sharpen)
  binarize?: boolean; // Convert to black/white for better bar detection
  gamma?: number; // 0.5-2.5 (1 = no change)
}

const DEFAULT_OPTIONS: PreprocessingOptions = {
  contrast: 1.5,
  brightness: 15,
  saturation: 1.2,
  blur: 0.5,
  sharpen: 0.3,
  binarize: false,
  gamma: 1.2,
};

/**
 * Apply CLAHE (Contrast Limited Adaptive Histogram Equalization)
 * to improve local contrast without over-amplifying noise
 */
function applyCLAHE(
  imageData: ImageData,
  clipLimit: number = 2.0,
  gridSize: number = 8
): ImageData {
  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;
  const tileWidth = Math.ceil(width / gridSize);
  const tileHeight = Math.ceil(height / gridSize);

  // Create a copy for processing
  const result = new ImageData(
    new Uint8ClampedArray(data),
    width,
    height
  );
  const resultData = result.data;

  // Process each tile
  for (let ty = 0; ty < gridSize; ty++) {
    for (let tx = 0; tx < gridSize; tx++) {
      const x0 = tx * tileWidth;
      const y0 = ty * tileHeight;
      const x1 = Math.min(x0 + tileWidth, width);
      const y1 = Math.min(y0 + tileHeight, height);

      // Build histogram for this tile
      const histogram = new Uint32Array(256);
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const idx = (y * width + x) * 4;
          const gray =
            (data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114) | 0;
          histogram[gray]++;
        }
      }

      // Apply clip limit
      const tileSize = (x1 - x0) * (y1 - y0);
      const limit = Math.ceil(clipLimit * (tileSize / 256));
      let excess = 0;
      for (let i = 0; i < 256; i++) {
        if (histogram[i] > limit) {
          excess += histogram[i] - limit;
          histogram[i] = limit;
        }
      }

      // Redistribute excess
      const increment = excess / 256;
      for (let i = 0; i < 256; i++) {
        histogram[i] += increment;
      }

      // Apply equalization to this tile
      let sum = 0;
      const lut = new Uint8Array(256);
      for (let i = 0; i < 256; i++) {
        sum += histogram[i];
        lut[i] = Math.round((sum / tileSize) * 255);
      }

      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const idx = (y * width + x) * 4;
          const gray =
            (data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114) | 0;
          const enhanced = lut[gray];
          resultData[idx] = enhanced;
          resultData[idx + 1] = enhanced;
          resultData[idx + 2] = enhanced;
        }
      }
    }
  }

  return result;
}

/**
 * Apply Gaussian blur using box blur approximation
 */
function applyGaussianBlur(
  imageData: ImageData,
  radius: number
): ImageData {
  if (radius <= 0) return imageData;

  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;
  const r = Math.floor(radius);
  const result = new Uint8ClampedArray(data);

  // Horizontal pass
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      let count = 0;
      for (let dx = -r; dx <= r; dx++) {
        const nx = Math.min(Math.max(x + dx, 0), width - 1);
        const idx = (y * width + nx) * 4;
        sum += data[idx];
        count++;
      }
      const idx = (y * width + x) * 4;
      const avg = Math.round(sum / count);
      result[idx] = avg;
      result[idx + 1] = avg;
      result[idx + 2] = avg;
    }
  }

  // Vertical pass
  const temp = new Uint8ClampedArray(result);
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      let sum = 0;
      let count = 0;
      for (let dy = -r; dy <= r; dy++) {
        const ny = Math.min(Math.max(y + dy, 0), height - 1);
        const idx = (ny * width + x) * 4;
        sum += temp[idx];
        count++;
      }
      const idx = (y * width + x) * 4;
      const avg = Math.round(sum / count);
      result[idx] = avg;
      result[idx + 1] = avg;
      result[idx + 2] = avg;
    }
  }

  return new ImageData(result, width, height);
}

/**
 * Apply unsharp mask for sharpening
 */
function applySharpen(
  imageData: ImageData,
  amount: number
): ImageData {
  if (amount <= 0) return imageData;

  const blurred = applyGaussianBlur(imageData, 1);
  const data = imageData.data;
  const blurData = blurred.data;
  const width = imageData.width;
  const height = imageData.height;
  const result = new Uint8ClampedArray(data);

  for (let i = 0; i < data.length; i += 4) {
    const delta = data[i] - blurData[i];
    result[i] = Math.min(255, Math.max(0, data[i] + delta * amount));
    result[i + 1] = Math.min(255, Math.max(0, data[i + 1] + (data[i + 1] - blurData[i + 1]) * amount));
    result[i + 2] = Math.min(255, Math.max(0, data[i + 2] + (data[i + 2] - blurData[i + 2]) * amount));
  }

  return new ImageData(result, width, height);
}

/**
 * Convert to grayscale and apply simple binarization
 */
function binarizeImage(imageData: ImageData): ImageData {
  const data = imageData.data;
  const result = new Uint8ClampedArray(data);

  // Calculate global threshold using Otsu's method
  const histogram = new Uint32Array(256);
  for (let i = 0; i < data.length; i += 4) {
    const gray = Math.round(
      data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114
    );
    histogram[gray]++;
  }

  let sum = 0;
  for (let i = 0; i < 256; i++) {
    sum += i * histogram[i];
  }

  let sumB = 0;
  let countB = 0;
  let maxVariance = 0;
  let threshold = 0;

  for (let t = 0; t < 256; t++) {
    countB += histogram[t];
    if (countB === 0) continue;

    const countF = data.length / 4 - countB;
    if (countF === 0) break;

    sumB += t * histogram[t];
    const meanB = sumB / countB;
    const meanF = (sum - sumB) / countF;

    const variance = countB * countF * Math.pow(meanB - meanF, 2);
    if (variance > maxVariance) {
      maxVariance = variance;
      threshold = t;
    }
  }

  // Apply binarization
  for (let i = 0; i < data.length; i += 4) {
    const gray = Math.round(
      data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114
    );
    const value = gray > threshold ? 255 : 0;
    result[i] = value;
    result[i + 1] = value;
    result[i + 2] = value;
  }

  return new ImageData(result, imageData.width, imageData.height);
}

/**
 * Apply gamma correction
 */
function applyGamma(imageData: ImageData, gamma: number): ImageData {
  if (gamma === 1) return imageData;

  const data = imageData.data;
  const result = new Uint8ClampedArray(data);
  const invGamma = 1 / gamma;

  for (let i = 0; i < data.length; i += 4) {
    result[i] = Math.round(Math.pow(data[i] / 255, invGamma) * 255);
    result[i + 1] = Math.round(Math.pow(data[i + 1] / 255, invGamma) * 255);
    result[i + 2] = Math.round(Math.pow(data[i + 2] / 255, invGamma) * 255);
  }

  return new ImageData(result, imageData.width, imageData.height);
}

/**
 * Main preprocessing function
 * Applies multiple filters to enhance barcode readability
 */
export function preprocessImage(
  imageData: ImageData,
  options: Partial<PreprocessingOptions> = {}
): ImageData {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let result = new ImageData(
    new Uint8ClampedArray(imageData.data),
    imageData.width,
    imageData.height
  );

  // 1. Apply gamma correction first
  if (opts.gamma !== 1) {
    result = applyGamma(result, opts.gamma);
  }

  // 2. Apply CLAHE for adaptive contrast enhancement
  result = applyCLAHE(result, 2.0, 8);

  // 3. Apply brightness and contrast via simple formula
  if (opts.brightness !== 0 || opts.contrast !== 1) {
    const data = result.data;
    const temp = new Uint8ClampedArray(data);
    for (let i = 0; i < data.length; i += 4) {
      let r = temp[i];
      let g = temp[i + 1];
      let b = temp[i + 2];

      // Apply contrast
      r = Math.round((r - 128) * opts.contrast + 128);
      g = Math.round((g - 128) * opts.contrast + 128);
      b = Math.round((b - 128) * opts.contrast + 128);

      // Apply brightness
      r += opts.brightness;
      g += opts.brightness;
      b += opts.brightness;

      data[i] = Math.min(255, Math.max(0, r));
      data[i + 1] = Math.min(255, Math.max(0, g));
      data[i + 2] = Math.min(255, Math.max(0, b));
    }
  }

  // 4. Reduce blur if needed (already minimal)
  if (opts.blur > 0) {
    result = applyGaussianBlur(result, opts.blur);
  }

  // 5. Apply sharpening
  if (opts.sharpen > 0) {
    result = applySharpen(result, opts.sharpen);
  }

  // 6. Binarize if needed
  if (opts.binarize) {
    result = binarizeImage(result);
  }

  return result;
}

/**
 * Apply preprocessing directly to a canvas
 */
export function preprocessCanvas(
  canvas: HTMLCanvasElement,
  options: Partial<PreprocessingOptions> = {}
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const processed = preprocessImage(imageData, options);
  ctx.putImageData(processed, 0, 0);
}

/**
 * Preprocess from video element
 */
export function preprocessVideoFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  options: Partial<PreprocessingOptions> = {}
): ImageData | null {
  const ctx = canvas.getContext('2d');
  if (!ctx || video.readyState < 2) return null;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Ensure main canvas dimensions match video (output size)
  const vw = video.videoWidth || canvas.clientWidth || 640;
  const vh = video.videoHeight || canvas.clientHeight || 480;
  canvas.width = vw;
  canvas.height = vh;

  // Perform heavy preprocessing on a downscaled offscreen canvas to reduce CPU
  // work and avoid freezing on high-resolution cameras.
  const MAX_PROCESS_DIM = 640; // max width/height for preprocessing
  const scale = Math.min(1, MAX_PROCESS_DIM / Math.max(vw || 1, vh || 1));
  const pw = Math.max(1, Math.floor(vw * scale));
  const ph = Math.max(1, Math.floor(vh * scale));

  const off = document.createElement('canvas');
  off.width = pw;
  off.height = ph;
  const offCtx = off.getContext('2d');
  if (!offCtx) return null;

  offCtx.imageSmoothingEnabled = true;
  offCtx.imageSmoothingQuality = 'high';

  // Draw scaled-down frame, preprocess there, then upscale result to main canvas
  offCtx.drawImage(video, 0, 0, pw, ph);
  const imageData = offCtx.getImageData(0, 0, pw, ph);
  const processed = preprocessImage(imageData, options);
  try {
    offCtx.putImageData(processed, 0, 0);
  } catch (e) {
    // If putImageData fails (very large), try drawing processed pixels via an
    // intermediate canvas image. Fallback kept minimal.
    offCtx.clearRect(0, 0, pw, ph);
    offCtx.drawImage(video, 0, 0, pw, ph);
  }

  // Draw the processed (downscaled) frame back to the main canvas scaled up
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(off, 0, 0, canvas.width, canvas.height);

  return processed;
}
